import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, DollarSign, Target, RefreshCw, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#FF6600', '#FF8533', '#FFB380', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B'];

const AcquisitionAnalytics = ({ token }) => {
  const [channelData, setChannelData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [channelRes, campaignRes, timelineRes] = await Promise.all([
        axios.get(`${API}/analytics/acquisition`, { headers }),
        axios.get(`${API}/analytics/acquisition/campaigns`, { headers }),
        axios.get(`${API}/analytics/acquisition/timeline?days=${timeRange}`, { headers })
      ]);
      
      setChannelData(channelRes.data);
      setCampaignData(campaignRes.data);
      setTimelineData(timelineRes.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Failed to load analytics');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [token, timeRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={fetchAnalytics}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const summary = channelData?.summary || { total_users: 0, paid_users: 0, overall_conversion_rate: 0 };
  const channels = channelData?.channels || [];
  const campaigns = campaignData?.campaigns || [];
  const timeline = timelineData?.timeline || [];

  // Prepare data for charts
  const channelChartData = channels.slice(0, 8).map(c => ({
    name: c.source === 'direct' ? 'Direct' : c.source,
    users: c.total_users,
    paid: c.paid_users,
    conversion: c.conversion_rate
  }));

  const pieData = channels.slice(0, 6).map(c => ({
    name: c.source === 'direct' ? 'Direct' : c.source,
    value: c.total_users
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Acquisition Analytics</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Track where your users come from</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            data-testid="time-range-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="total-users-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Users</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{summary.total_users.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="paid-users-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Paid Users</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{summary.paid_users.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="conversion-rate-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Target className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Conversion Rate</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{summary.overall_conversion_rate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="channels-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Active Channels</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{channels.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Timeline Chart */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="timeline-chart">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">Signups Over Time</h3>
          {timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="signups" 
                  stroke="#FF6600" 
                  strokeWidth={2}
                  name="Signups"
                />
                <Line 
                  type="monotone" 
                  dataKey="paid_conversions" 
                  stroke="#22C55E" 
                  strokeWidth={2}
                  name="Paid"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">
              No data available
            </div>
          )}
        </div>

        {/* Channel Distribution Pie */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="channel-distribution-chart">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">Channel Distribution</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#FF6600"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Channel Performance Bar Chart */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="channel-performance-chart">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4">Channel Performance</h3>
        {channelChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={channelChartData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="users" fill="#FF6600" name="Total Users" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" fill="#22C55E" name="Paid Users" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400">
            No data available
          </div>
        )}
      </div>

      {/* Channel Details Table */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="channel-table">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4">Channel Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Source</th>
                <th className="text-left py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Medium</th>
                <th className="text-right py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Users</th>
                <th className="text-right py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Paid</th>
                <th className="text-right py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Conv %</th>
              </tr>
            </thead>
            <tbody>
              {channels.length > 0 ? channels.map((channel, idx) => (
                <tr key={idx} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700">
                  <td className="py-3 px-2 text-gray-900 dark:text-white capitalize">
                    {channel.source === 'direct' ? 'Direct' : channel.source}
                  </td>
                  <td className="py-3 px-2 text-gray-600 dark:text-gray-300">
                    {channel.medium === 'none' ? '-' : channel.medium}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-900 dark:text-white font-medium">
                    {channel.total_users.toLocaleString()}
                  </td>
                  <td className="py-3 px-2 text-right text-green-600 font-medium">
                    {channel.paid_users.toLocaleString()}
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      channel.conversion_rate >= 50 ? 'bg-green-100 text-green-700' :
                      channel.conversion_rate >= 30 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {channel.conversion_rate}%
                    </span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-400">
                    No channel data yet. Users will appear here as they sign up.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign Performance */}
      {campaigns.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-testid="campaigns-table">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">Campaign Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Campaign</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Sources</th>
                  <th className="text-right py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Users</th>
                  <th className="text-right py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Paid</th>
                  <th className="text-right py-3 px-2 font-semibold text-gray-600 dark:text-gray-300">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign, idx) => (
                  <tr key={idx} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700">
                    <td className="py-3 px-2 text-gray-900 dark:text-white font-medium">
                      {campaign.campaign}
                    </td>
                    <td className="py-3 px-2 text-gray-600 dark:text-gray-300">
                      {campaign.sources.filter(s => s).join(', ') || '-'}
                    </td>
                    <td className="py-3 px-2 text-right text-gray-900 dark:text-white">
                      {campaign.total_users.toLocaleString()}
                    </td>
                    <td className="py-3 px-2 text-right text-green-600 font-medium">
                      {campaign.paid_users.toLocaleString()}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        campaign.conversion_rate >= 50 ? 'bg-green-100 text-green-700' :
                        campaign.conversion_rate >= 30 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {campaign.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* UTM Tracking Guide */}
      <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-xl border border-orange-200 dark:border-orange-800" data-testid="utm-guide">
        <h3 className="font-bold text-orange-800 dark:text-orange-300 mb-2">UTM Tracking Guide</h3>
        <p className="text-sm text-orange-700 dark:text-orange-400 mb-3">
          Add UTM parameters to your marketing links to track where users come from:
        </p>
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg font-mono text-xs overflow-x-auto">
          <code className="text-gray-700 dark:text-gray-300">
            https://nutrifitness.us?<span className="text-orange-600">utm_source</span>=facebook&<span className="text-orange-600">utm_medium</span>=paid&<span className="text-orange-600">utm_campaign</span>=spring_promo
          </code>
        </div>
        <ul className="mt-3 text-sm text-orange-700 dark:text-orange-400 space-y-1">
          <li>• <strong>utm_source</strong>: Traffic source (facebook, google, tiktok, email)</li>
          <li>• <strong>utm_medium</strong>: Marketing medium (paid, organic, influencer, affiliate)</li>
          <li>• <strong>utm_campaign</strong>: Campaign name (spring_promo, black_friday)</li>
        </ul>
      </div>
    </div>
  );
};

export default AcquisitionAnalytics;
